import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Shared, cached production-settings store (gold prices + FX + fees). Fetched once
// per session and broadcast to every consumer so a top-bar rate change updates
// the create forms live. Mirrors hooks/useReferenceLists.
//
// FX: when `fx_auto` is true (default) the effective `usd_to_eur_rate` is the live
// ECB rate from /api/fx/rate (cached daily server-side); when false the manual
// value is used. Either way consumers just read `settings.usd_to_eur_rate`.
const SETTINGS_DEFAULTS = {
  gold_18k_eur_per_gram: 65, gold_14k_eur_per_gram: 50, gold_9k_eur_per_gram: 33,
  gold_prices: [
    { karat: "18K", js: 65, bsd: 65 },
    { karat: "14K", js: 50, bsd: 50 },
  ],
  usd_to_eur_rate: 0.92, fx_auto: true, fx_live: null, fx_as_of: null, fx_stale: false,
  gold_tracking_mode: "detailed",
};
let _cache = null;
let _inflight = null;
const _subs = new Set();

// Merge live ECB FX into the cached settings when fx_auto is on.
async function _applyLiveFx(base) {
  if (base.fx_auto === false) return base;
  try {
    const { data } = await api.get("/fx/rate");
    if (data?.usd_eur) {
      return { ...base, usd_to_eur_rate: data.usd_eur, fx_live: data.usd_eur, fx_as_of: data.as_of, fx_stale: !!data.stale };
    }
  } catch { /* keep manual/default rate */ }
  return base;
}

export function useProductionSettings() {
  const [settings, setSettings] = useState(_cache || SETTINGS_DEFAULTS);
  useEffect(() => {
    let mounted = true;
    const update = (d) => { if (mounted) setSettings(d); };
    _subs.add(update);
    if (_cache) {
      update(_cache);
    } else if (!_inflight) {
      _inflight = api.get("/production/settings")
        .then(async ({ data }) => {
          let merged = { ...SETTINGS_DEFAULTS, ...(data || {}) };
          merged = await _applyLiveFx(merged);
          _cache = merged;
          _subs.forEach((fn) => fn(_cache));
        })
        .catch(() => { _cache = SETTINGS_DEFAULTS; })
        .finally(() => { _inflight = null; });
    }
    return () => { mounted = false; _subs.delete(update); };
  }, []);
  return settings;
}

// Push freshly-saved settings into the shared cache (call after PUT /production/settings).
export async function primeProductionSettings(data) {
  let merged = { ...SETTINGS_DEFAULTS, ...(data || {}) };
  merged = await _applyLiveFx(merged);
  _cache = merged;
  _subs.forEach((fn) => fn(_cache));
}

// Force a fresh ECB rate fetch (server bypasses its throttle) and broadcast it.
// Returns the new {usd_eur, as_of, stale}. No-op merge when fx is manual.
export async function refreshFx() {
  const { data } = await api.get("/fx/rate", { params: { force: true } });
  const base = _cache || SETTINGS_DEFAULTS;
  if (data?.usd_eur && base.fx_auto !== false) {
    _cache = { ...base, usd_to_eur_rate: data.usd_eur, fx_live: data.usd_eur, fx_as_of: data.as_of, fx_stale: !!data.stale };
    _subs.forEach((fn) => fn(_cache));
  }
  return data;
}


// Helper: resolve the €/g gold price for a given karat + stock type from gold_prices.
export function goldPriceFor(settings, karat, stockType) {
  const list = settings?.gold_prices || [];
  const row = list.find((g) => String(g.karat).toUpperCase() === String(karat).toUpperCase());
  if (!row) return 0;
  return Number((String(stockType).toUpperCase() === "BSD" ? row.bsd : row.js) || 0);
}
